import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { productQueries } from "@surgia/api/src/queryStore/admin/product";
import { UpdateTenantProductPayload } from "@surgia/ui/dtos/request";

interface UseUpdateProductProps {
  onSuccess?: (data: any, variables: UpdateProductVariables) => void;
  onError?: (error: any) => void;
  onProductConfigUpdate?: (productConfigs: Record<string, any>) => void;
  parentProductId: string;
  tenantId: string;
}

interface UpdateProductVariables {
  tenantProductId: string;
  payload: UpdateTenantProductPayload;
}

export const useUpdateProduct = ({
  onSuccess,
  onError,
  onProductConfigUpdate,
}: UseUpdateProductProps) => {
  const queryClient = useQueryClient();

  const updateProductMutation = useMutation({
    mutationFn: async ({
      tenantProductId,
      payload,
    }: UpdateProductVariables) => {
      const mutation =
        productQueries.mutations.updateTenantProduct(tenantProductId);
      return mutation.mutationFn(payload);
    },
    onSuccess: (data, variables) => {
      toast.success("Product configuration saved successfully!");

      // Invalidate queries to get fresh data
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenant", "products"],
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes("variations");
        },
      });
      // Call custom onSuccess if provided
      if (onSuccess) {
        onSuccess(data, variables);
      }
    },
    onError: (error) => {
      toast.error("Failed to save product configuration. Please try again.");

      // Call custom onError if provided
      if (onError) {
        onError(error);
      }
    },
  });

  const updateProduct = async (params: UpdateProductVariables) => {
    return updateProductMutation.mutateAsync(params);
  };

  const updateProductConfigs = (
    productConfigs: Record<string, any>,
    tenantProductId: string
  ) => {
    const updatedConfigs = { ...productConfigs };
    const productId = Object.keys(productConfigs).find(
      (key) =>
        productConfigs[key].tenantProductId === tenantProductId ||
        productConfigs[key].tenantInfoId === tenantProductId
    );

    if (productId) {
      // Mark this config as having initial values
      updatedConfigs[productId] = {
        ...updatedConfigs[productId],
        hasInitialValues: true,
        // Reset the change tracking
        hasChanges: false,
      };
    }

    // Call the callback to update product configs in the parent component
    if (onProductConfigUpdate) {
      onProductConfigUpdate(updatedConfigs);
    }

    return updatedConfigs;
  };

  return {
    updateProduct,
    updateProductConfigs,
    updateProductMutation,
    isUpdating: updateProductMutation.isPending,
    updatingProductId: updateProductMutation.variables?.tenantProductId || null,
    isError: updateProductMutation.isError,
    error: updateProductMutation.error,
  };
};
